const statusUtils = {

  // Grievance status mapping
  GRIEVANCE_STATUS_MAPPING: {
    '1': 'Grievance Raised By ESM',
    '2': 'Grievance Accepted By Aresa Center & Forwarded for Redresal',
    '3': 'Grievance Rejected By Aresa Center',
    '4': 'In Progress',
    '5': 'Awaiting Documents from ESM',
    '6': 'Document Resubmitted by ESM',
    '7': 'Completed',
    '8': 'Feeback submitted By ESM & Closed',
    '9': 'Re-opened by ESM'
  },
  
  // Grant status mapping (new)
  GRANT_STATUS_MAPPING: {
    '10': 'Grant Applied By ESM',
    '11': 'Grant Accepted By Aresa Center & Forwarded for Redresal',
    '12': 'Grant Rejected By Aresa Center',
    '13': 'Grant Approval in progress',
    '14': 'Awaiting Documents from ESM',
    '15': 'Document Resubmitted by ESM',
    '16': 'Grant Approved by Branch User',
    '17': 'Grant Rejected By Branch User'
  },
  
  // Role status mapping for grievances
  ROLE_STATUS_MAPPING: {
    // ESM User (Role 1)
    1: ['1', '6', '9'],
    
    // ARESA Center (Role 2)
    2: ['2', '3'], // ARESA can only set Accepted or Rejected status
    
    // Branch User (Role 3)
    3: ['4', '5', '7'],
    
    // DGAR Admin (Role 4)
    4: ['1', '2', '3', '4', '5', '6', '7', '8', '9'] // Can view all statuses
  },
  
  // Role status mapping for grants
  GRANT_ROLE_STATUS_MAPPING: {
    // ESM User (Role 1)
    1: ['10', '15'],
    
    // ARESA Center (Role 2)
    2: ['11', '12', '13', '14'], 
    
    // Branch User (Role 3)
    3: ['16', '17'],
    
    // DGAR Admin (Role 4)
    4: ['10', '11', '12', '13', '14', '15', '16', '17'] // Can view all statuses
  },
  
  // Role-specific display text overrides
  ROLE_STATUS_DISPLAY: {
    // ARESA Center (Role 2)
    2: {
      '2': 'Grievance Accepted & Forwarded to Branch for Redresal',
      '3': 'Rejected by ARESA Center',
      '11': 'Grant Accepted & Forwarded to Branch for Redresal',
      '12': 'Grant Rejected by ARESA Center'
    }
  },
  
  // Get status name with optional role-specific display text
  getStatusName: (statusId, userRole = null) => {
    // Check if there's a role-specific display name
    if (userRole && statusUtils.ROLE_STATUS_DISPLAY[userRole] && 
        statusUtils.ROLE_STATUS_DISPLAY[userRole][statusId]) {
      return statusUtils.ROLE_STATUS_DISPLAY[userRole][statusId];
    }
    
    // Check if it's a grievance status
    if (statusUtils.GRIEVANCE_STATUS_MAPPING[statusId]) {
      return statusUtils.GRIEVANCE_STATUS_MAPPING[statusId];
    }
    
    // Check if it's a grant status
    if (statusUtils.GRANT_STATUS_MAPPING[statusId]) {
      return statusUtils.GRANT_STATUS_MAPPING[statusId];
    }
    
    // Otherwise return unknown
    return 'Unknown Status';
  },
  
  // Get styling for status display
  getStatusStyle: (status) => {
    const statusLower = typeof status === 'string' ? status.toLowerCase() : '';
    
    // For status IDs, convert to name first
    if (typeof status === 'string') {
      if (statusUtils.GRIEVANCE_STATUS_MAPPING[status]) {
        return statusUtils.getStatusStyle(statusUtils.GRIEVANCE_STATUS_MAPPING[status]);
      } else if (statusUtils.GRANT_STATUS_MAPPING[status]) {
        return statusUtils.getStatusStyle(statusUtils.GRANT_STATUS_MAPPING[status]);
      }
    }
    
    // Match by status name
    if (statusLower.includes('raised') || statusLower.includes('submitted') || 
        statusLower.includes('applied')) {
      return { className: 'status-new', icon: 'bi-file-earmark-plus' };
    } else if (statusLower.includes('rejected')) {
      return { className: 'status-rejected', icon: 'bi-x-circle' };
    } else if (statusLower.includes('closed') || 
               statusLower.includes('approved') || 
               statusLower.includes('completed')) {
      return { className: 'status-resolved', icon: 'bi-check-circle' };
    } else if (statusLower.includes('progress')) {
      return { className: 'status-progress', icon: 'bi-hourglass-split' };
    } else if (statusLower.includes('awaiting')) {
      return { className: 'status-pending', icon: 'bi-clock' };
    } else if (statusLower.includes('forward')) {
      return { className: 'status-forwarded', icon: 'bi-arrow-right-circle' };
    } else if (statusLower.includes('accepted')) {
      return { className: 'status-accepted', icon: 'bi-check2-circle' };
    } else if (statusLower.includes('resubmitted')) {
      return { className: 'status-new', icon: 'bi-upload' };
    } else {
      return { className: 'status-default', icon: 'bi-circle' };
    }
  },
  
  // Filter status list based on role permissions
  filterStatusListByRole: (statusList, userRole, isGrant = false) => {
    if (!statusList || !Array.isArray(statusList) || statusList.length === 0) {
      return [];
    }
    
    const roleMapping = isGrant 
      ? statusUtils.GRANT_ROLE_STATUS_MAPPING[userRole]
      : statusUtils.ROLE_STATUS_MAPPING[userRole];
      
    if (!roleMapping) {
      return statusList; // If role not defined, return all
    }
    
    const allowedStatusIds = roleMapping;
    
    // Filter status list based on role permissions
    const filteredList = statusList
      .filter(status => {
        const statusId = status.status_id || status.STATUS_ID;
        return allowedStatusIds.includes(statusId);
      });
    
    // Apply role-specific display names if available
    return filteredList.map(status => {
      // Create a new object to avoid modifying the original
      const newStatus = { ...status };
      const statusId = status.status_id || status.STATUS_ID;
      
      // Apply role-specific display name if available
      if (statusUtils.ROLE_STATUS_DISPLAY[userRole] && 
          statusUtils.ROLE_STATUS_DISPLAY[userRole][statusId]) {
        newStatus.status = statusUtils.ROLE_STATUS_DISPLAY[userRole][statusId];
      }
      
      return newStatus;
    });
  },
  
  // Check if user can update to a specific status
  canUpdateToStatus: (userRole, statusId, isGrant = false) => {
    const roleMapping = isGrant 
      ? statusUtils.GRANT_ROLE_STATUS_MAPPING[userRole]
      : statusUtils.ROLE_STATUS_MAPPING[userRole];
      
    if (!roleMapping) {
      return false;
    }
    
    return roleMapping.includes(statusId.toString());
  },

  // Check if a grievance can be forwarded to branch
  canForwardToBranch: (grievance, userRole) => {
    // Only ARESA can forward to Branch
    if (userRole !== 2) {
      return false;
    }
    
    // Check if grievance has been forwarded
    if (grievance?.isForwarded === "1" || grievance?.ISFORWARDED === "1") {
      return false;
    }
    
    // Don't allow forwarding if already in certain statuses
    const nonForwardableStatusIds = ['3', '7', '8']; // Rejected, Completed, Closed
    
    // Check by status ID if available, otherwise check by name
    if (grievance?.statusId || grievance?.STATUS_ID) {
      const statusId = grievance.statusId || grievance.STATUS_ID;
      return !nonForwardableStatusIds.includes(statusId);
    }
    
    const nonForwardableStatuses = [
      'Grievance Rejected By Aresa Center',
      'Completed', 
      'Feeback submitted By ESM & Closed',
      'Rejected by ARESA Center'
    ];
    
    return !nonForwardableStatuses.includes(grievance?.status);
  },
  
  // Check if a grant can be forwarded to branch
  canForwardGrantToBranch: (grant, userRole) => {
    // Only ARESA can forward to Branch
    if (userRole !== 2) {
      return false;
    }
    
    // Check if grant has been forwarded
    if (grant?.isForwarded === "1" || grant?.ISFORWARDED === "1") {
      return false;
    }
    
    // Don't allow forwarding if already in certain statuses
    const nonForwardableStatusIds = ['12', '16', '17']; // Rejected, Approved, Rejected by Branch
    
    // Check by status ID if available, otherwise check by name
    if (grant?.statusId || grant?.STATUS_ID) {
      const statusId = grant.statusId || grant.STATUS_ID;
      return !nonForwardableStatusIds.includes(statusId);
    }
    
    const nonForwardableStatuses = [
      'Grant Rejected By Aresa Center',
      'Grant Approved by Branch User',
      'Grant Rejected By Branch User',
      'Grant Rejected by ARESA Center'
    ];
    
    return !nonForwardableStatuses.includes(grant?.status);
  },
  
  // Check if status indicates approval
  isApprovedStatus: (statusId) => {
    const approvedStatusIds = ['7', '16']; // Completed, Grant Approved
    return approvedStatusIds.includes(statusId);
  },
  
  // Check if status indicates rejection
  isRejectedStatus: (statusId) => {
    const rejectedStatusIds = ['3', '12', '17']; // Grievance Rejected, Grant Rejected by ARESA, Grant Rejected by Branch
    return rejectedStatusIds.includes(statusId);
  },
  
  // Check if status indicates pending state
  isPendingStatus: (statusId) => {
    const pendingStatusIds = ['1', '2', '4', '5', '6', '9', '10', '11', '13', '14', '15'];
    return pendingStatusIds.includes(statusId);
  },
  
  // Map old status IDs to new grant status IDs (for compatibility)
  mapLegacyToGrantStatus: (legacyStatusId) => {
    const mapping = {
      '1': '10', // Raised -> Applied
      '2': '11', // Accepted & Forwarded -> Accepted & Forwarded
      '3': '12', // Rejected by ARESA -> Rejected by ARESA
      '4': '13', // In Progress -> Approval in progress
      '5': '14', // Awaiting Documents -> Awaiting Documents
      '6': '15', // Document Resubmitted -> Document Resubmitted
      '7': '16'  // Completed -> Approved
    };
    
    return mapping[legacyStatusId] || legacyStatusId;
  }
};

export default statusUtils;